import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  useTheme,
  Fade,
  Slide,
  Zoom,
  styled,
  Collapse,
  Paper,
  Avatar,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  alpha,
  useMediaQuery,
  Card,
  CardContent,
  Tabs,
  Tab,
  InputAdornment,
  InputBase,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { getFirestore, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import {
  MailOutline,
  Facebook,
  Twitter,
  Instagram,
  Restaurant,
  PointOfSale,
  Storefront,
  Analytics,
  RocketLaunch,
  Timer,
  EmojiEvents,
  Stars,
  AutoAwesome,
  WhatsApp,
  LinkedIn,
  YouTube,
  CheckCircle,
  ArrowForward,
  Diamond,
  Speed,
  Security,
  CloudDone,
  TrendingUp,
  AttachMoney,
  ReceiptLong,
  CalendarToday,
  MoreVert,
  Download,
  Print,
  Search,
  Close,
  Person,
  Business,
  LocationOn,
  ShoppingBag,
  Receipt,
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';

// ==================== ANIMATIONS ====================

const floating = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const scaleUp = keyframes`
  0% { transform: scale(0.9); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(99, 102, 241, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(-100px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideInRight = keyframes`
  from {
    transform: translateX(100px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

// ==================== STYLED COMPONENTS ====================

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  background: '#f3f4f6',
  padding: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
}));

const HeaderPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 0,
  background: 'white',
  borderBottom: '1px solid',
  borderColor: 'divider',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  marginBottom: theme.spacing(3),
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  background: 'white',
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(3),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  }
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  }
}));

const FeatureCard = styled(Paper)(({ theme, color }) => ({
  background: 'white',
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(3),
  textAlign: 'center',
  transition: 'all 0.3s ease',
  border: `1px solid ${alpha(color || theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px ${alpha(color || theme.palette.primary.main, 0.15)}`,
    border: `1px solid ${alpha(color || theme.palette.primary.main, 0.3)}`,
  }
}));

const CountdownCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const CountdownNumberBox = styled(Paper)(({ theme }) => ({
  background: 'white',
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(2),
  textAlign: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  }
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: 'white',
  borderRadius: theme.shape.borderRadius * 2,
  padding: '12px 32px',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  '&:active': {
    transform: 'translateY(0)',
  }
}));

const SearchPaper = styled(Paper)(({ theme }) => ({
  p: '2px 4px',
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: theme.shape.borderRadius * 2,
  background: 'white',
}));

const IconAvatar = styled(Avatar)(({ theme, bgcolor }) => ({
  background: bgcolor || alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  width: 56,
  height: 56,
  marginBottom: theme.spacing(2),
}));

// ==================== MAIN COMPONENT ====================

const MarketplaceComingSoon = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [email, setEmail] = useState('');
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [showExportMenu, setShowExportMenu] = useState(null);
  const db = getFirestore();

  // Launch date
  const LAUNCH_DATE = new Date('2025-07-20T00:00:00');

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = Date.now();
      const difference = LAUNCH_DATE - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
        return true;
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return false;
      }
    };

    const timerActive = calculateTimeLeft();
    let timer;
    if (timerActive) {
      timer = setInterval(() => {
        if (!calculateTimeLeft()) {
          clearInterval(timer);
        }
      }, 1000);
    }

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (email) {
      setLoading(true);
      try {
        await addDoc(collection(db, "subscribers"), {
          email: email,
          createdAt: serverTimestamp()
        });

        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 3000);
        setEmail('');
      } catch (error) {
        console.error("Error adding document: ", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const features = [
    { icon: <Restaurant />, title: 'POS Intelligent', text: 'Système de point de vente avancé avec IA et analytics en temps réel', color: '#6366f1' },
    { icon: <Storefront />, title: 'Marketplace Digital', text: 'Connectez-vous aux fournisseurs locaux et internationaux', color: '#8b5cf6' },
    { icon: <PointOfSale />, title: 'Gestion des Stocks', text: 'Suivi en temps réel avec alertes automatiques', color: '#ec489a' },
    { icon: <Analytics />, title: 'Analytics Avancé', text: 'Tableaux de bord interactifs et rapports personnalisés', color: '#14b8a6' },
    { icon: <Speed />, title: 'Performance', text: 'Optimisation des opérations et réduction des coûts', color: '#f59e0b' },
    { icon: <Security />, title: 'Sécurité', text: 'Données chiffrées et sauvegardes automatiques', color: '#3b82f6' },
  ];

  const stats = [
    { value: '500+', label: 'Restaurants', icon: <Restaurant />, color: '#6366f1', trend: '+25%' },
    { value: '1000+', label: 'Fournisseurs', icon: <Storefront />, color: '#8b5cf6', trend: '+40%' },
    { value: '50k+', label: 'Commandes/mois', icon: <PointOfSale />, color: '#14b8a6', trend: '+15%' },
    { value: '99.9%', label: 'Uptime', icon: <CloudDone />, color: '#10b981', trend: '99.9%' },
  ];

  const testimonials = [
    { name: 'Ahmed Ben Ali', role: 'Restaurant Le Chef', quote: 'Une révolution dans la gestion de restaurant!', rating: 5 },
    { name: 'Sara Mansour', role: 'Café Parisien', quote: 'L\'interface est incroyablement intuitive.', rating: 5 },
    { name: 'Karim Said', role: 'Pizza Express', quote: 'Le meilleur investissement pour notre restaurant.', rating: 5 },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f3f4f6' }}>
      {/* Header */}
      <HeaderPaper elevation={0}>
        <Box sx={{ p: 2, maxWidth: 1400, margin: 'auto', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Avatar sx={{ bgcolor: '#2563eb', width: 48, height: 48 }}>
              <Storefront />
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Marketplace ERP Restaurant
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Plateforme complète de gestion de restaurant
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <GradientButton
                startIcon={<RocketLaunch />}
                onClick={() => document.getElementById('newsletter').scrollIntoView({ behavior: 'smooth' })}
              >
                Accès anticipé
              </GradientButton>

              <IconButton onClick={(e) => setShowExportMenu(e.currentTarget)} sx={{ bgcolor: 'rgba(0,0,0,0.04)' }}>
                <MoreVert />
              </IconButton>

              <Menu
                anchorEl={showExportMenu}
                open={Boolean(showExportMenu)}
                onClose={() => setShowExportMenu(null)}
              >
                <MenuItem onClick={() => window.print()}>
                  <ListItemIcon><Print fontSize="small" /></ListItemIcon>
                  <ListItemText>Imprimer la page</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => alert('Fonctionnalité à venir')}>
                  <ListItemIcon><Download fontSize="small" /></ListItemIcon>
                  <ListItemText>Télécharger la brochure</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            sx={{ mt: 2 }}
          >
            <Tab label="Présentation" />
            <Tab label="Fonctionnalités" />
            <Tab label="Témoignages" />
          </Tabs>
        </Box>
      </HeaderPaper>

      <StyledContainer maxWidth="lg">
        {/* Tab 1: Presentation */}
        {tabValue === 0 && (
          <Fade in timeout={500}>
            <Box>
              {/* Hero Section */}
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Chip
                  icon={<AutoAwesome sx={{ fontSize: 16 }} />}
                  label="Coming Soon - Lancement Juillet 2025"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    mb: 3,
                    borderRadius: 2,
                  }}
                />
                <Typography
                  variant={isMobile ? "h3" : "h1"}
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 2,
                  }}
                >
                  ERP Restaurant Ecosystem
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
                >
                  Révolutionnez la gestion de votre restaurant avec notre plateforme tout-en-un.
                  Gérez vos commandes, stocks, fournisseurs et analyses en un seul endroit.
                </Typography>
                <GradientButton
                  size="large"
                  startIcon={<RocketLaunch />}
                  onClick={() => document.getElementById('newsletter').scrollIntoView({ behavior: 'smooth' })}
                >
                  Rejoindre la liste d'attente
                </GradientButton>
              </Box>

              {/* Stats Cards */}
              <Grid container spacing={3} sx={{ mb: 6 }}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} sm={3} key={index}>
                    <Zoom in timeout={(index + 1) * 200}>
                      <StatCard>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color }}>
                                {stat.value}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {stat.label}
                              </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                              {stat.icon}
                            </Avatar>
                          </Box>
                          {stat.trend && (
                            <Chip
                              label={`+${stat.trend}`}
                              size="small"
                              sx={{
                                mt: 1,
                                bgcolor: alpha('#10b981', 0.1),
                                color: '#10b981',
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </CardContent>
                      </StatCard>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>

              {/* Countdown Timer */}
              <CountdownCard sx={{ mb: 6 }}>
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 4 }}>
                  <Timer sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Lancement dans
                  </Typography>
                  <RocketLaunch sx={{ color: theme.palette.secondary.main, animation: `${floating} 2s ease-in-out infinite` }} />
                </Stack>

                <Grid container spacing={isMobile ? 1 : 3} justifyContent="center">
                  {Object.entries(timeLeft).map(([unit, value]) => (
                    <Grid item key={unit}>
                      <CountdownNumberBox>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                          {Math.floor(value).toString().padStart(2, '0')}
                        </Typography>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                          {unit}
                        </Typography>
                      </CountdownNumberBox>
                    </Grid>
                  ))}
                </Grid>
              </CountdownCard>

              {/* Search Demo */}
              <GlassCard sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Découvrez nos fonctionnalités
                </Typography>
                <SearchPaper>
                  <IconButton sx={{ p: '10px' }}>
                    <Search />
                  </IconButton>
                  <InputBase
                    sx={{ ml: 1, flex: 1 }}
                    placeholder="Rechercher une fonctionnalité..."
                  />
                  <IconButton sx={{ p: '10px' }}>
                    <Close />
                  </IconButton>
                </SearchPaper>
              </GlassCard>
            </Box>
          </Fade>
        )}

        {/* Tab 2: Features */}
        {tabValue === 1 && (
          <Fade in timeout={500}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                Fonctionnalités Innovantes
              </Typography>
              <Grid container spacing={3}>
                {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Zoom in timeout={(index + 1) * 200}>
                      <FeatureCard color={feature.color}>
                        <IconAvatar bgcolor={alpha(feature.color, 0.1)} sx={{ color: feature.color, mx: 'auto' }}>
                          {feature.icon}
                        </IconAvatar>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.text}
                        </Typography>
                      </FeatureCard>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>

              {/* Additional Features */}
              <Box sx={{ mt: 6 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                  Avantages Exclusifs
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { icon: <Diamond />, text: 'Support prioritaire 24/7' },
                    { icon: <EmojiEvents />, text: 'Certification officielle' },
                    { icon: <Stars />, text: 'Mises à jour gratuites' },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={4} key={index}>
                      <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, mx: 'auto', mb: 1 }}>
                          {item.icon}
                        </Avatar>
                        <Typography variant="body2">{item.text}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Fade>
        )}

        {/* Tab 3: Testimonials */}
        {tabValue === 2 && (
          <Fade in timeout={500}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                Ce que disent nos clients
              </Typography>
              <Grid container spacing={3}>
                {testimonials.map((testimonial, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Zoom in timeout={(index + 1) * 200}>
                      <GlassCard sx={{ textAlign: 'center' }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 64, height: 64, mx: 'auto', mb: 2 }}>
                          <Person />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                          {testimonial.role}
                        </Typography>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                          "{testimonial.quote}"
                        </Typography>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Stars key={i} sx={{ fontSize: 16, color: '#f59e0b' }} />
                          ))}
                        </Stack>
                      </GlassCard>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Fade>
        )}

        {/* Newsletter Section */}
        <Box id="newsletter" sx={{ mt: 6 }}>
          <GlassCard sx={{ p: 4, textAlign: 'center' }}>
            <Collapse in={submitted}>
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <CheckCircle sx={{ color: theme.palette.success.main }} />
                <Typography sx={{ color: theme.palette.success.main }}>
                  Merci ! Vous êtes maintenant sur la liste d'attente.
                </Typography>
              </Paper>
            </Collapse>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Obtenez un accès anticipé
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Soyez parmi les premiers à essayer notre plateforme et bénéficiez d'offres exclusives
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Votre email professionnel"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    type="email"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><MailOutline sx={{ color: theme.palette.primary.main }} /></InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <GradientButton
                    fullWidth
                    type="submit"
                    disabled={loading}
                    sx={{ height: 56 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : (
                      <>
                        Rejoindre
                        <ArrowForward sx={{ ml: 1 }} />
                      </>
                    )}
                  </GradientButton>
                </Grid>
              </Grid>
            </form>
          </GlassCard>
        </Box>

        {/* Social Links */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Suivez notre aventure
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            {[
              { icon: <Facebook sx={{ fontSize: 28 }} />, color: '#1877f2', label: 'Facebook' },
              { icon: <Twitter sx={{ fontSize: 28 }} />, color: '#1da1f2', label: 'Twitter' },
              { icon: <Instagram sx={{ fontSize: 28 }} />, color: '#e4405f', label: 'Instagram' },
              { icon: <LinkedIn sx={{ fontSize: 28 }} />, color: '#0077b5', label: 'LinkedIn' },
              { icon: <YouTube sx={{ fontSize: 28 }} />, color: '#ff0000', label: 'YouTube' },
              { icon: <WhatsApp sx={{ fontSize: 28 }} />, color: '#25d366', label: 'WhatsApp' },
            ].map((social, index) => (
              <Zoom in timeout={(index + 1) * 100} key={index}>
                <Tooltip title={social.label} arrow>
                  <IconButton
                    href="#"
                    target="_blank"
                    sx={{
                      color: social.color,
                      transition: 'all 0.3s ease',
                      bgcolor: alpha(social.color, 0.1),
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        bgcolor: alpha(social.color, 0.2),
                      }
                    }}
                  >
                    {social.icon}
                  </IconButton>
                </Tooltip>
              </Zoom>
            ))}
          </Stack>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" color="text.secondary">
            © 2025 ERP Restaurant Ecosystem. Tous droits réservés.
          </Typography>
        </Box>
      </StyledContainer>
    </Box>
  );
};

export default MarketplaceComingSoon;